import { useCallback, useEffect, useRef, useState } from 'react';

export interface ThumbnailState {
  id: string;
  file: File;
  thumbnail: string | null;
  duration: string | null;
  fileSize: number;
  processedAt: number | null;
  isLoading: boolean;
  error: string | null;
}

export interface UseMultipleVideoThumbnailsOptions {
  width?: number;
  batchSize?: number;
  onProgress?: (completed: number, total: number) => void;
  onComplete?: (results: ThumbnailState[]) => void;
  onError?: (error: string, fileId: string) => void;
}

export const useMultipleVideoThumbnails = (
  files: File[],
  options: UseMultipleVideoThumbnailsOptions = {}
) => {
  const {
    width = 300,
    batchSize = 3,
    onProgress,
    onComplete,
    onError
  } = options;

  const [thumbnails, setThumbnails] = useState<ThumbnailState[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const processingRef = useRef<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const processAllFilesRef = useRef<() => Promise<void>>();
  const resetRef = useRef<() => void>();
  const cancelProcessingRef = useRef<() => void>();
  const lastProcessedFilesRef = useRef<string>('');
  const thumbnailsRef = useRef<ThumbnailState[]>([]);

  const generateThumbnailForFile = useCallback(async (file: File, id: string): Promise<ThumbnailState> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      let objectURL: string | null = null;

      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;

      objectURL = URL.createObjectURL(file);
      video.src = objectURL;

      const cleanup = () => {
        if (objectURL) URL.revokeObjectURL(objectURL);
        video.remove();
        canvas.remove();
      };

      video.onloadedmetadata = () => {
        const duration = video.duration;
        const seekTime = Math.min(1, duration - 0.1);
        video.currentTime = seekTime;
      };

      video.onerror = () => {
        cleanup();
        reject(new Error(`Failed to load video: ${file.name}`));
      };

      video.onseeked = () => {
        try {
          const scale = width / video.videoWidth;
          canvas.width = width;
          canvas.height = video.videoHeight * scale;

          if (!ctx) throw new Error('Canvas not supported');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          const thumbnail = canvas.toDataURL('image/jpeg', 0.7);

          const minutes = Math.floor(video.duration / 60);
          const seconds = Math.floor(video.duration % 60);
          const durationText = `${minutes}:${seconds.toString().padStart(2, '0')}`;

          cleanup();

          resolve({
            id,
            file,
            thumbnail,
            duration: durationText,
            fileSize: file.size,
            processedAt: Date.now(),
            isLoading: false,
            error: null
          });
        } catch (err) {
          cleanup();
          reject(err);
        }
      };
    });
  }, [width]);

  const processBatch = useCallback(async (filesToProcess: File[], startIndex: number) => {
    const promises = filesToProcess.map(async (file, index) => {
      const id = `${startIndex + index}`;
      
      // Update state to show loading
      setThumbnails(prev => prev.map(thumb => 
        thumb.id === id ? { ...thumb, isLoading: true, error: null } : thumb
      ));

      try {
        const result = await generateThumbnailForFile(file, id);
        
        setThumbnails(prev => prev.map(thumb => 
          thumb.id === id ? result : thumb
        ));

        setCompletedCount(prev => {
          const newCount = prev + 1;
          setTotalCount(currentTotal => {
            onProgress?.(newCount, currentTotal);
            return currentTotal;
          });
          return newCount;
        });

        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        
        setThumbnails(prev => prev.map(thumb => 
          thumb.id === id ? { 
            ...thumb, 
            isLoading: false, 
            error: errorMessage 
          } : thumb
        ));

        onError?.(errorMessage, id);
        setCompletedCount(prev => prev + 1);
        
        return {
          id,
          file,
          thumbnail: null,
          duration: null,
          fileSize: file.size,
          processedAt: null,
          isLoading: false,
          error: errorMessage
        };
      }
    });

    return Promise.all(promises);
  }, [generateThumbnailForFile, onProgress, onError]);

  const processAllFiles = useCallback(async () => {
    if (files.length === 0 || processingRef.current) {
      return;
    }

    // Create a signature of the files to check if we've already processed them
    const filesSignature = files.map(f => `${f.name}-${f.size}-${f.lastModified}`).join('|');
    
    if (lastProcessedFilesRef.current === filesSignature) {
      return;
    }

    // Find which files need processing (new files or files without thumbnails)
    setThumbnails(currentThumbnails => {
      const newThumbnails: ThumbnailState[] = files.map((file, index) => {
        const id = index.toString();
        const existingThumbnail = currentThumbnails.find(thumb => 
          thumb.file.name === file.name && 
          thumb.file.size === file.size && 
          thumb.file.lastModified === file.lastModified
        );
        
        if (existingThumbnail) {
          // Keep existing thumbnail but update the ID to match new position
          return { ...existingThumbnail, id };
        }
        
        // New file that needs processing
        return {
          id,
          file,
          thumbnail: null,
          duration: null,
          fileSize: file.size,
          processedAt: null,
          isLoading: false,
          error: null
        };
      });
      
      return newThumbnails;
    });

    // Get current thumbnails to check which files need processing
    const currentThumbnails = thumbnailsRef.current;
    const filesToProcess = files.filter((file) => {
      const existingThumbnail = currentThumbnails.find(thumb => 
        thumb.file.name === file.name && 
        thumb.file.size === file.size && 
        thumb.file.lastModified === file.lastModified &&
        thumb.thumbnail
      );
      return !existingThumbnail;
    });
    
    const completedCount = currentThumbnails.filter(t => t.thumbnail).length;

    if (filesToProcess.length === 0) {
      // All files already have thumbnails
      lastProcessedFilesRef.current = filesSignature;
      return;
    }

    lastProcessedFilesRef.current = filesSignature;
    processingRef.current = true;
    setIsProcessing(true);
    setCompletedCount(completedCount);
    setTotalCount(files.length);

    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();

    try {
      // Process only new files in batches
      for (let i = 0; i < filesToProcess.length; i += batchSize) {
        if (abortControllerRef.current?.signal.aborted) break;

        const batch = filesToProcess.slice(i, i + batchSize);
        // Find the actual index in the full files array for each file in the batch
        const batchWithIndices = batch.map(file => {
          const actualIndex = files.findIndex(f => 
            f.name === file.name && 
            f.size === file.size && 
            f.lastModified === file.lastModified
          );
          return { file, actualIndex };
        });
        
        await processBatch(batchWithIndices.map(b => b.file), batchWithIndices[0].actualIndex);
      }

      if (!abortControllerRef.current?.signal.aborted) {
        // Get the current thumbnails state for the callback
        setThumbnails(currentThumbnails => {
          onComplete?.(currentThumbnails);
          return currentThumbnails;
        });
      }
    } catch (error) {
      console.error('Error processing video thumbnails:', error);
    } finally {
      processingRef.current = false;
      setIsProcessing(false);
      abortControllerRef.current = null;
    }
  }, [files, batchSize, processBatch, onComplete]);

  const cancelProcessing = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    processingRef.current = false;
    setIsProcessing(false);
    abortControllerRef.current = null;
  }, []);

  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    processingRef.current = false;
    setIsProcessing(false);
    setThumbnails([]);
    setCompletedCount(0);
    setTotalCount(0);
    lastProcessedFilesRef.current = '';
    abortControllerRef.current = null;
  }, []);

  // Update thumbnails ref whenever thumbnails state changes
  useEffect(() => {
    thumbnailsRef.current = thumbnails;
  }, [thumbnails]);

  // Store function references to avoid dependency issues
  processAllFilesRef.current = processAllFiles;
  resetRef.current = reset;
  cancelProcessingRef.current = cancelProcessing;

  // Auto-process when files change
  useEffect(() => {
    if (files.length > 0) {
      processAllFilesRef.current?.();
    } else {
      resetRef.current?.();
    }

    return () => {
      cancelProcessingRef.current?.();
    };
  }, [files]); // Only depend on files array

  return {
    thumbnails,
    isProcessing,
    completedCount,
    totalCount,
    progress: totalCount > 0 ? (completedCount / totalCount) * 100 : 0,
    cancelProcessing,
    reset,
    reprocess: processAllFiles
  };
};
